import { Box, Grid, styled, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { endDateFromTimePointType } from '../../../api/timeranges/utils';
import {
  TimePointSelectorContext,
  useDomainContext,
  useFilterSectionsContext,
  useGlobalFilterContext,
  useGlobalLocaleContext,
  useGlobalSegmentationLevel1Context,
  useLatestPreferencesContext,
  useSegmentationSectionsContext,
  useTimePointSelectorContext,
} from '../../../context/contexts';
import { StoryboardDateUtils } from '../../../settings/dashboard-configs/utils';
import { format, getMomentDate, monthEnd, now } from '../../../utils-date';
import {
  filterBarGrey,
  filterBarRed,
  segmentationMainColor,
} from '../../theme';
import { ChartComponent } from '../charts/ChartComponent';
import { FilterBar } from '../filter/filterbar/FilterBar';
import { Markdown } from '../misc/Markdown';
import { CalendarYearMonthlyTimesliderGranularity } from '../timeslider/constants';
import { useTimePointSelectorWithPersistence } from '../timeslider/time-point-selector-hooks';
import { TimeSlider } from '../timeslider/TimeSlider';
import { Months, TimePointSelectorState } from '../timeslider/types';
import { useDashboardPageOptionsContext } from './contexts';
import { PageConfig } from './types';

const StyledWrapper = styled('div')`
  height: 100%;
  margin-left: 3rem;
`;

const headerHeight = '60px';
const filterBarHeight = '60px';

const CustomPageInner = ({ pageConfig }: { pageConfig: PageConfig }) => {
  const filterHandle = useGlobalFilterContext();
  const segmentationHandle = useGlobalSegmentationLevel1Context();
  const theme = useTheme();
  const pageOptions = useDashboardPageOptionsContext();
  const { timeSlider, filterTray, segmentationLevel1 } = pageOptions;
  const filterTrayEnabled = filterTray?.enabled ?? true;
  const segmentationLevel1TrayEnabled = segmentationLevel1?.enabled ?? true;
  const locale = useGlobalLocaleContext();
  const filterSections = useFilterSectionsContext();
  const segmentationSections = useSegmentationSectionsContext();
  const [timePointSelectorState] = useTimePointSelectorContext();
  const domainPreferences = useLatestPreferencesContext();
  const firstMonthOfYear =
    domainPreferences.finYearStartMonth ?? Months.January;
  const dateFromTimeSlider = endDateFromTimePointType(
    timePointSelectorState.date,
    timePointSelectorState.selectedGranularity,
    firstMonthOfYear
  );
  const defaultReferenceDate = format(monthEnd(now()));
  const { referenceDate = defaultReferenceDate } = pageConfig;
  const baseDateForPage = timeSlider?.enabled
    ? dateFromTimeSlider
    : referenceDate;
  // Without timeSlider being enabled, this will default to the current
  // month's date. If I don't have this step, this isn't guaranteed, since
  // available time ranges sometimes returns values only till previous month
  const storyboardDateUtils = useMemo(
    () => new StoryboardDateUtils(getMomentDate(baseDateForPage).valueOf()),
    [getMomentDate, baseDateForPage]
  );
  const pageComponents = pageConfig.components({
    storyboardDateUtils,
    domainPreferences,
  });

  return (
    <StyledWrapper>
      <Grid
        item
        xs
        container
        sx={{
          padding: 2,
          height: headerHeight,
          overflow: 'hidden',
        }}
      >
        <Grid item container flexWrap="nowrap">
          <Grid
            item
            container
            xs={timeSlider?.enabled ? 4 : 12}
            wrap="nowrap"
            alignItems={'center'}
          >
            <Typography variant="h5" color="textPrimary">
              {pageConfig.title[locale.selected]}
            </Typography>
          </Grid>
          {timeSlider?.enabled && <TimeSlider type={timeSlider.type} />}
        </Grid>
      </Grid>
      {filterTrayEnabled || segmentationLevel1TrayEnabled ? (
        <Grid
          item
          container
          sx={{ height: filterBarHeight, overflow: 'auto' }}
          justifyContent={'space-between'}
        >
          {filterTrayEnabled ? (
            <Grid item xs={8}>
              <FilterBar
                filterHandle={filterHandle}
                filterSections={filterSections}
              />
            </Grid>
          ) : null}
          {segmentationLevel1TrayEnabled ? (
            <Grid item xs={4}>
              <FilterBar
                filterHandle={segmentationHandle}
                filterSections={segmentationSections}
                options={{
                  justifyContent: 'flex-end',
                  filterPillColors: {
                    default: segmentationMainColor,
                    stale: filterBarGrey,
                    notEqual: filterBarRed,
                  },
                  allowDelete: true,
                }}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}
      <Grid
        item
        container
        direction={'column'}
        sx={{
          height: `calc(100% - ${headerHeight} - ${
            filterTrayEnabled || segmentationLevel1TrayEnabled
              ? filterBarHeight
              : '0px'
          })`,
        }}
        wrap="nowrap"
      >
        <Box sx={{ flex: '0 0 auto' }}>
          {pageConfig.header && <Markdown markdown={pageConfig.header} />}
        </Box>
        <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
          <Grid
            container
            spacing={0} // No gap between grid items
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${pageConfig.gridCols ?? 3}, minmax(0,1fr))`,
              gridTemplateRows: `repeat(${pageConfig.gridRows ?? 2}, minmax(0,1fr))`,
              paddingLeft: '0.5rem',
              gridAutoRows: '1fr',
              height: '100%',
              [theme.breakpoints.down('lg')]: {
                gridTemplateColumns: `repeat(2, 1fr)`,
                height: 'unset',
              },
            }}
          >
            {pageComponents.map((component, index) => (
              <ChartComponent
                key={`${component.chartId}-${index}`}
                componentConfig={component}
                pageOptions={pageOptions}
              />
            ))}
          </Grid>
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>
          {pageConfig.footer && <Markdown markdown={pageConfig.footer} />}
        </Box>
      </Grid>
    </StyledWrapper>
  );
};

const CustomPageWithReferenceDate = ({
  pageConfig,
  referenceDate,
}: {
  pageConfig: PageConfig;
  referenceDate: string;
}) => {
  const [timePointSelectorState] = useTimePointSelectorContext();
  const modifiedState: TimePointSelectorState = {
    ...timePointSelectorState,
    selectedGranularity: CalendarYearMonthlyTimesliderGranularity,
    date: getMomentDate(referenceDate).toDate(),
  };
  const domain = useDomainContext();
  const localTimepointSelectorHandle = useTimePointSelectorWithPersistence(
    domain,
    modifiedState
  );
  return (
    <TimePointSelectorContext.Provider value={localTimepointSelectorHandle}>
      <CustomPageInner pageConfig={pageConfig} />
    </TimePointSelectorContext.Provider>
  );
};

const CustomPage = ({ pageConfig }: { pageConfig: PageConfig }) => {
  const { referenceDate } = pageConfig;
  if (referenceDate && getMomentDate(referenceDate).isValid()) {
    return (
      <CustomPageWithReferenceDate
        referenceDate={referenceDate}
        pageConfig={pageConfig}
      />
    );
  } else {
    return <CustomPageInner pageConfig={pageConfig} />;
  }
};
export default CustomPage;
